<template>
  <div class="dropzone-container"
       :class="{ 'is-drag-active': isDragActive }"
       :style="{ minHeight: height ? height + 'px' : null, width: width ? width + 'px' : null }"
       v-bind="getRootProps()">
    <input v-bind="getInputProps()"/>

    <div class="dropzone-prompt" :class="{ compact: !displayText }">
      <span class="upload-icon">
        <i class="fa-solid fa-cloud-arrow-up"></i>
      </span>
      <div v-if="isDragActive" class="prompt-title">Dateien hier ablegen …</div>
      <template v-else-if="displayText">
        <div class="prompt-title">{{ text }}</div>
        <div class="prompt-hint">oder <span class="prompt-link">Datei auswählen</span></div>
      </template>
      <div v-else class="prompt-hint">
        Klicken oder Dateien hierher ziehen, um die Auswahl zu ersetzen
      </div>
    </div>

    <ul v-if="Array.isArray(modelValue) && modelValue.length" class="file-list d-flex">
      <li v-for="(file, index) in modelValue" :key="index">
        <span class="file-icon" :style="{ '--file-icon': `url(${getFileType(file.name).imgSrc || '/icons/file.svg'})` }"></span>
        <div class="file-name" :title="file.name">{{ file.name }}</div>
        <div class="file-size">{{ formatSize(file.size) }}</div>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { getFileType } from '@/shared/functions';
import { computed, defineEmits, defineProps, toRefs } from 'vue';
import { useDropzone } from 'vue3-dropzone';

const props = defineProps({
  modelValue: {
    type: [ String, Number, null ],
    required: true,
  },
  multiple: {
    type: Boolean,
    default: true, // Allow multiple files by default
  },
  text: {
    type: String,
    default: 'Ziehen Sie einige Dateien per Drag & Drop hierher, oder klicken Sie zum Auswählen',
  },
  height: {
    type: [ String, Number ],
  },
  width: {
    type: [ String, Number ],
  },
});

const modelValue  = toRefs(props)[ 'modelValue' ];
const emit        = defineEmits([ 'update:modelValue' ]);

const displayText = computed(() => {
  const value = modelValue.value;

  if (!value) {
    return true
  }

  // Single file object
  if (value instanceof File) {
    return false;
  }

  // Array of files
  if (Array.isArray(value) && value.length > 0 && value[ 0 ] instanceof File) {
    return false;
  }

  // Not a file or unrecognized
  return true;
});

const onDrop = (acceptedFiles, rejectedFiles) => {
  emit('update:modelValue', acceptedFiles);
};

const { getRootProps, getInputProps, isDragActive } = useDropzone({
  onDrop,
  multiple: props.multiple, // Enable or disable multiple file selection
});

const formatSize = (size) => {
  return size < 1024
      ? `${size} B`
      : size < 1024 * 1024
          ? `${(size / 1024).toFixed(1)} KB`
          : `${(size / (1024 * 1024)).toFixed(1)} MB`;
};
</script>

<style scoped>
.dropzone-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 24px;
  border: 2px dashed color-mix(in srgb, var(--primary) 35%, white);
  border-radius: 12px;
  text-align: center;
  background-color: color-mix(in srgb, var(--primary) 4%, white);
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s, box-shadow 0.2s;
}

.dropzone-container:hover {
  border-color: var(--primary);
  background-color: color-mix(in srgb, var(--primary) 8%, white);
}

.dropzone-container.is-drag-active {
  border-style: solid;
  border-color: var(--primary);
  background-color: color-mix(in srgb, var(--primary) 12%, white);
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--primary) 15%, transparent);
}

.dropzone-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.upload-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 4px;
  border-radius: 50%;
  font-size: 24px;
  color: var(--primary);
  background-color: color-mix(in srgb, var(--primary) 12%, white);
  transition: transform 0.2s;
}

.dropzone-container:hover .upload-icon,
.dropzone-container.is-drag-active .upload-icon {
  transform: translateY(-3px);
}

.prompt-title {
  font-size: 15px;
  font-weight: 600;
  color: #343a40;
}

.prompt-hint {
  font-size: 13px;
  color: #6c757d;
}

.prompt-link {
  color: var(--primary);
  font-weight: 600;
  text-decoration: underline;
}

/* Smaller prompt once files are selected, so the file list stays in focus */
.dropzone-prompt.compact {
  flex-direction: row;
  justify-content: center;
}

.dropzone-prompt.compact .upload-icon {
  width: 32px;
  height: 32px;
  margin-bottom: 0;
  font-size: 14px;
}

.file-list {
  gap: 16px;
  justify-content: center;
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
  flex-wrap: wrap;
}

/* Icon drawn as a mask so it takes the company primary color */
.file-icon {
  display: inline-block;
  width: 50px;
  height: 50px;
  background-color: var(--primary);
  -webkit-mask: var(--file-icon) no-repeat center / contain;
  mask: var(--file-icon) no-repeat center / contain;
}

.file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-size {
  font-size: 12px;
  color: #6c757d;
}

.file-list li {
  width: 160px;
  padding: 10px 8px;
  border: 1px solid #e3e6ea;
  border-radius: 8px;
  background-color: #fff;
  font-size: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.file-list li .file-icon {
  width: 40px;
  height: 40px;
  margin-bottom: 6px;
}
</style>
  